import { ChartConfig } from "@/components/ui/chart";
import { DailyStatRow, ByPluginRow } from "@/services/scrapingStatsApi";

// Types d'évènements de scraping (scraping_events.event_type côté back).
// Source de vérité partagée entre la page scraping-stats et le cockpit
// worker-tasks (graphiques débit + répartition par worker).
export const EVENT_TYPES = [
  "invitation",
  "message",
  "candidate",
  "company",
  "school",
  "search",
] as const;

export type EventType = (typeof EVENT_TYPES)[number];

export const EVENT_LABELS: Record<string, string> = {
  invitation: "Invitations",
  message: "Messages",
  candidate: "Candidats",
  company: "Entreprises",
  school: "Écoles",
  search: "Recherches",
};

export const EVENT_COLORS: Record<string, string> = {
  invitation: "hsl(var(--chart-5))",
  message: "hsl(210 70% 50%)",
  candidate: "hsl(var(--chart-1))",
  company: "hsl(var(--chart-2))",
  school: "hsl(var(--chart-3))",
  search: "hsl(var(--chart-4))",
};

// ChartConfig recharts (theme-aware via var(--color-<key>)).
export const scrapingChartConfig: ChartConfig = {
  invitation: { label: "Invitations", color: EVENT_COLORS.invitation },
  message: { label: "Messages", color: EVENT_COLORS.message },
  candidate: { label: "Candidats", color: EVENT_COLORS.candidate },
  company: { label: "Entreprises", color: EVENT_COLORS.company },
  school: { label: "Écoles", color: EVENT_COLORS.school },
  search: { label: "Recherches", color: EVENT_COLORS.search },
};

// ---------------- Transforms partagés (scraping-stats + cockpit worker) -----

export type DailyChartPoint = { date: string } & Record<EventType, number>;

// Agrège les lignes (date, event_type, total) en points empilables par date.
export function buildDailyChartData(dailyData: DailyStatRow[]): DailyChartPoint[] {
  const byDate: Record<string, Record<string, number>> = {};
  for (const row of dailyData) {
    if (!byDate[row.date]) byDate[row.date] = {};
    byDate[row.date][row.event_type] = (byDate[row.date][row.event_type] || 0) + row.total;
  }
  return Object.entries(byDate)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, counts]) => ({
      date,
      ...Object.fromEntries(EVENT_TYPES.map((t) => [t, counts[t] || 0])),
    })) as DailyChartPoint[];
}

export type PluginRow = { worker_id: string; total: number } & Record<EventType, number>;

// Regroupe les lignes (worker_id, event_type, total) par worker, triées par
// volume total décroissant.
export function buildPluginRows(pluginData: ByPluginRow[]): PluginRow[] {
  const grouped: Record<string, Record<string, number>> = {};
  for (const row of pluginData) {
    if (!grouped[row.worker_id]) grouped[row.worker_id] = {};
    grouped[row.worker_id][row.event_type] = row.total;
  }
  return Object.entries(grouped)
    .map(([worker_id, counts]) => ({
      worker_id,
      ...Object.fromEntries(EVENT_TYPES.map((t) => [t, counts[t] || 0])),
      total: Object.values(counts).reduce((a, b) => a + b, 0),
    }))
    .sort((a, b) => b.total - a.total) as PluginRow[];
}
